<template lang="pug">
div
  v-card(flat)
    v-toolbar.white(:id="'uppy-select_' + id" Zdense card style="cursor: pointer")
      v-toolbar-title.body-2 {{ label }} {{ files.length > 0 ? '(' + files.length + ')' : '' }}
      v-spacer
      v-btn(@click.prevent="" icon)
        v-img(src="/static/icons/green_plus.jpg")
  v-input(ref="input" v-show="!hasValue" v-model="hasValue" :rules="[(v) => this.required ? (!!v || $tf('fileUploader.filesRequired')) : true]")
  v-list(v-if="files.length")
    template(v-for="(file, index) in files")
      v-list-tile
        v-list-tile-avatar
          v-icon.grey.lighten-1.white--text fa-md {{ getIcon(file.type) }}
        v-list-tile-content
          v-list-tile-title {{ file.fileName }}
        v-list-tile-action
          v-list-tile-sub-title {{ prettyBytes(file.size) }}
      v-divider(:key="index")
</template>

<script>
import { mapGetters } from 'vuex'

import Uppy from '@uppy/core'
import Dashboard from '@/components/utils/FileUploader/dashboard/index'
import XHRUpload from '@/components/utils/FileUploader/xhr-upload/index'
import Webcam from '@/components/utils/FileUploader/webcam/index'
import '@/components/utils/FileUploader/dashboard/style/style.css'
import '@/components/utils/FileUploader/webcam/style/style.css'

import MimeTypeIcons from '@/utils/mimeTypeIcons'
import PrettyBytes from 'prettier-bytes'

import minioAPI from '@/services/minio'

export default {
  props: {
    id: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: () => this.$tf('fileUploader.label'),
    },
    value: {
      type: Array,
      default: null,
    },
    allowedFileTypes: {
      type: Array,
      required: true,
    },
    maxFileSize: {
      type: Number,
      default: 10,
    },
    minNumberOfFiles: {
      type: Number,
      default: 1,
    },
    maxNumberOfFiles: {
      type: Number,
      default: 10,
    },
    required: {
      type: Boolean,
      default: true,
    },
    debug: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      uppy: null,
      files: [],
      removedFile: false,
    }
  },

  computed: {
    ...mapGetters({
      profile: 'auth/profile',
    }),
    hasValue() {
      if (this.files.length > 0) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.$refs.input.hasFocused = true
        return true
      } return null
    },
  },

  watch: {
    'value'(value) {
      if (value === null) {
        this.uppy.reset()
        this.files = []
      } else if (this.files.length === 0 && !this.removedFile) {
        this.restoreState(value)
      }
      this.removedFile = false
    },
  },

  mounted() {
    this.uppy = Uppy({
      debug: this.debug,
      restrictions: {
        maxFileSize: 1024 * 1024 * this.maxFileSize,
        maxNumberOfFiles: this.maxNumberOfFiles,
        minNumberOfFiles: this.minNumberOfFiles,
        allowedFileTypes: this.allowedFileTypes,
      },
      locale: {
        strings: {
          youCanOnlyUploadX: {
            0: this.$tf('fileUploader.youCanOnlyUploadX.0'),
            1: this.$tf('fileUploader.youCanOnlyUploadX.1'),
          },
          youHaveToAtLeastSelectX: {
            0: this.$tf('fileUploader.youHaveToAtLeastSelectX.0'),
            1: this.$tf('fileUploader.youHaveToAtLeastSelectX.1'),
          },
          exceedsSize: this.$tf('fileUploader.exceedsSize'),
          youCanOnlyUploadFileTypes: this.$tf('fileUploader.youCanOnlyUploadFileTypes'),
          companionError: this.$tf('fileUploader.companionError'),
          failedToUpload: this.$tf('fileUploader.failedToUpload'),
          noInternetConnection: this.$tf('fileUploader.noInternetConnection'),
          connectedToInternet: this.$tf('fileUploader.connectedToInternet'),
        },
      },
    })
    this.uppy.use(XHRUpload, {
      locale: {
        timedOut: this.$tf('fileUploader.timedOut'),
      },
    })
    this.uppy.use(Dashboard, {
      trigger: `#uppy-select_${this.id}`,
      showLinkToFileUploadResult: false,
      showProgressDetails: true,
      proudlyDisplayPoweredByUppy: false,
      note: this.$tpf('fileUploader.note', { maxNumberOfFiles: this.maxNumberOfFiles, maxFileSize: this.prettyBytes(this.maxFileSize * 1024 * 1024) }),
      metaFields: [
        { id: 'note', name: this.$tf('fileUploader.metaFields.name'), placeholder: this.$tf('fileUploader.metaFields.placeholder') },
      ],
      locale: {
        strings: {
          selectToUpload: this.$tf('fileUploader.selectToUpload'),
          closeModal: this.$tf('fileUploader.closeModal'),
          upload: this.$tf('fileUploader.upload'),
          importFrom: this.$tf('fileUploader.importFrom'),
          addingMoreFiles: this.$tf('fileUploader.addingMoreFiles'),
          addMoreFiles: this.$tf('fileUploader.addMoreFiles'),
          dashboardWindowTitle: this.$tf('fileUploader.dashboardWindowTitle'),
          dashboardTitle: this.$tf('fileUploader.dashboardTitle'),
          copyLinkToClipboardSuccess: this.$tf('fileUploader.copyLinkToClipboardSuccess'),
          copyLinkToClipboardFallback: this.$tf('fileUploader.copyLinkToClipboardFallback'),
          copyLink: this.$tf('fileUploader.copyLink'),
          fileSource: this.$tf('fileUploader.fileSource'),
          done: this.$tf('fileUploader.done'),
          back: this.$tf('fileUploader.back'),
          name: this.$tf('fileUploader.name'),
          removeFile: this.$tf('fileUploader.removeFile'),
          editFile: this.$tf('fileUploader.editFile'),
          editing: this.$tf('fileUploader.editing'),
          edit: this.$tf('fileUploader.edit'),
          finishEditingFile: this.$tf('fileUploader.finishEditingFile'),
          saveChanges: this.$tf('fileUploader.saveChanges'),
          cancel: this.$tf('fileUploader.cancel'),
          localDisk: this.$tf('fileUploader.localDisk'),
          myDevice: this.$tf('fileUploader.myDevice'),
          dropPasteImport: this.$tf('fileUploader.dropPasteImport'),
          dropPaste: this.$tf('fileUploader.dropPaste'),
          browse: this.$tf('fileUploader.browse'),
          fileProgress: this.$tf('fileUploader.fileProgress'),
          numberOfSelectedFiles: this.$tf('fileUploader.numberOfSelectedFiles'),
          uploadAllNewFiles: this.$tf('fileUploader.uploadAllNewFiles'),
          emptyFolderAdded: this.$tf('fileUploader.emptyFolderAdded'),
          uploadComplete: this.$tf('fileUploader.uploadComplete'),
          resumeUpload: this.$tf('fileUploader.resumeUpload'),
          pauseUpload: this.$tf('fileUploader.pauseUpload'),
          retryUpload: this.$tf('fileUploader.retryUpload'),
          cancelUpload: this.$tf('fileUploader.cancelUpload'),
          xFilesSelected: {
            0: this.$tf('fileUploader.xFilesSelected.0'),
            1: this.$tf('fileUploader.xFilesSelected.1'),
          },
          uploadXFiles: {
            0: this.$tf('fileUploader.uploadXFiles.0'),
            1: this.$tf('fileUploader.uploadXFiles.1'),
          },
          uploadXNewFiles: {
            0: this.$tf('fileUploader.uploadXNewFiles.0'),
            1: this.$tf('fileUploader.uploadXNewFiles.1'),
          },
          folderAdded: {
            0: this.$tf('fileUploader.folderAdded.0'),
            1: this.$tf('fileUploader.folderAdded.1'),
          },
          selectXFiles: {
            0: this.$tf('fileUploader.selectXFiles.0'),
            1: this.$tf('fileUploader.selectXFiles.1'),
          },
          filesUploadedOfTotal: {
            0: this.$tf('fileUploader.filesUploadedOfTotal.0'),
            1: this.$tf('fileUploader.filesUploadedOfTotal.1'),
          },
          uploading: this.$tf('fileUploader.uploading'),
          complete: this.$tf('fileUploader.complete'),
          uploadFailed: this.$tf('fileUploader.uploadFailed'),
          pleasePressRetry: this.$tf('fileUploader.pleasePressRetry'),
          paused: this.$tf('fileUploader.paused'),
          error: this.$tf('fileUploader.error'),
          retry: this.$tf('fileUploader.retry'),
          pressToRetry: this.$tf('fileUploader.pressToRetry'),
          dataUploadedOfTotal: this.$tf('fileUploader.dataUploadedOfTotal'),
          xTimeLeft: this.$tf('fileUploader.xTimeLeft'),
          noFilesFound: this.$tf('fileUploader.noFilesFound'),
          logOut: this.$tf('fileUploader.logOut'),
        },
      },
    })
    this.uppy.use(Webcam, {
      target: Dashboard,
      modes: ['picture'],
      locale: {
        strings: {
          smile: this.$tf('fileUploader.smile'),
          takePicture: this.$tf('fileUploader.takePicture'),
          startRecording: this.$tf('fileUploader.startRecording'),
          stopRecording: this.$tf('fileUploader.stopRecording'),
          allowAccessTitle: this.$tf('fileUploader.allowAccessTitle'),
          allowAccessDescription: this.$tf('fileUploader.allowAccessDescription'),
        },
      },
    })

    this.uppy.on('complete', (result) => {
      for (const file of result.successful) {
        const item = {
          fileName: file.name,
          uuid: file.response.body.uuid,
          type: file.type,
          size: file.size,
          author: `${this.profile.firstName} ${this.profile.lastName}`,
          note: file.meta.note,
          uploadDate: new Date(),
        }
        const unsortedFiles = this.files
        unsortedFiles.push(item)
        unsortedFiles.sort((a, b) => ((a.uploadDate < b.uploadDate) ? 1 : ((b.uploadDate < a.uploadDate) ? -1 : 0)))
        this.files = unsortedFiles
        this.$emit('input', this.files)
      }
    })

    this.uppy.on('file-removed', (file) => {
      const removeIndex = this.files.findIndex(item => item.fileName === file.name)
      this.files.splice(removeIndex, 1)
      this.removedFile = true
      this.$emit('input', this.files)
    })

    this.restoreState(this.value)
  },

  beforeDestroy() {
    this.uppy.close()
  },

  methods: {
    prettyBytes(bytes) {
      return PrettyBytes(bytes)
    },
    getIcon(type) {
      return MimeTypeIcons.get(type)
    },
    restoreState(value) {
      if (value) {
        this.uppy.reset()
        this.files = value
        minioAPI.presignedGetObjectUrls(value).then((result) => {
          result.data.forEach((item) => {
            fetch(item.thumbnailUrl)
              .then(response => response.blob())
              .then((blob) => {
                this.uppy.addFile({
                  name: item.fileName,
                  type: item.type,
                  data: blob,
                  source: 'Local',
                  isRemote: false,
                })
              }).then(() => {
                Object.keys(this.uppy.state.files).forEach((fileID) => {
                  this.uppy.setFileState(fileID, {
                    progress: { uploadComplete: true, uploadStarted: true },
                  })
                })
              })
          })
        })
      }
    },
  },
}
</script>
<style lang="stylus">
</style>


// WEBPACK FOOTER //
// src/components/utils/FileUploader/FileUploader.vue